import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';

const DefaultLayout = () => import('@/layouts/DefaultLayout.vue');

const LoginPage = () => import('@/modules/auth/pages/LoginPage.vue');
const NoOfficeAccessPage = () => import('@/modules/auth/pages/NoOfficeAccessPage.vue');
const ForbiddenPage = () => import('@/modules/system/pages/ForbiddenPage.vue');
const NotFoundPage = () => import('@/modules/system/pages/NotFoundPage.vue');

const DashboardPage = () => import('@/modules/dashboard/pages/DashboardPage.vue');
const DriverListPage = () => import('@/modules/drivers/pages/DriverListPage.vue');
const DriverDetailPage = () => import('@/modules/drivers/pages/DriverDetailPage.vue');
const DriverCreatePage = () => import('@/modules/drivers/pages/DriverCreatePage.vue');
const DriverEditPage = () => import('@/modules/drivers/pages/DriverEditPage.vue');
const VehicleListPage = () => import('@/modules/fleet/pages/VehicleListPage.vue');
const VehicleDetailPage = () => import('@/modules/fleet/pages/VehicleDetailPage.vue');
const VehicleCreatePage = () => import('@/modules/fleet/pages/VehicleCreatePage.vue');
const WorkshopTaskListPage = () => import('@/modules/workshop/pages/WorkshopTaskListPage.vue');
const WorkshopTaskDetailPage = () => import('@/modules/workshop/pages/WorkshopTaskDetailPage.vue');
const WorkshopTaskCreatePage = () => import('@/modules/workshop/pages/WorkshopTaskCreatePage.vue');
const DamageListPage = () => import('@/modules/damage/pages/DamageListPage.vue');
const DamageDetailPage = () => import('@/modules/damage/pages/DamageDetailPage.vue');
const TicketListPage = () => import('@/modules/tickets/pages/TicketListPage.vue');
const TicketDetailPage = () => import('@/modules/tickets/pages/TicketDetailPage.vue');
const FuelListPage = () => import('@/modules/fuel/pages/FuelListPage.vue');
const FuelDetailPage = () => import('@/modules/fuel/pages/FuelDetailPage.vue');
const DispatcherCockpitPage = () => import('@/modules/dispatcher/pages/DispatcherCockpitPage.vue');
const DocumentsListPage = () => import('@/modules/documents/pages/DocumentsListPage.vue');
const AuditListPage = () => import('@/modules/audit/pages/AuditListPage.vue');
const StationsListPage = () => import('@/modules/stations/pages/StationsListPage.vue');
const AccommodationListPage = () => import('@/modules/accommodation/pages/AccommodationListPage.vue');
const AccommodationDetailPage = () => import('@/modules/accommodation/pages/AccommodationDetailPage.vue');
const SimsListPage = () => import('@/modules/sims/pages/SimsListPage.vue');
const SimDetailPage = () => import('@/modules/sims/pages/SimDetailPage.vue');
const PayrollListPage = () => import('@/modules/payroll/pages/PayrollListPage.vue');
const PayrollDetailPage = () => import('@/modules/payroll/pages/PayrollDetailPage.vue');
const ReportingPage = () => import('@/modules/reporting/pages/ReportingPage.vue');
const SettingsPage = () => import('@/modules/settings/pages/SettingsPage.vue');

// ComingSoonPage entfernt mit Phase 5D.8 (D-173 v1.1-Backlog Cleanup).

declare module 'vue-router' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface RouteMeta {
    public?: boolean;
    layout?: 'default' | 'auth' | 'blank';
    perm?: string | string[];
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { public: true, layout: 'auth', title: 'Anmelden' },
  },
  {
    path: '/no-office',
    name: 'NoOffice',
    component: NoOfficeAccessPage,
    meta: { public: true, layout: 'blank', title: 'Kein Office-Zugang' },
  },
  {
    path: '/',
    component: DefaultLayout,
    meta: { layout: 'default' },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: DashboardPage,
        meta: { title: 'Dashboard' },
      },
      {
        path: 'drivers',
        name: 'Drivers',
        component: DriverListPage,
        meta: { perm: ['driver.read', 'driver.read_own'], title: 'Fahrer' },
      },
      {
        path: 'drivers/new',
        name: 'DriverCreate',
        component: DriverCreatePage,
        meta: { perm: 'driver.create', title: 'Neuen Fahrer anlegen' },
      },
      {
        path: 'drivers/:id',
        name: 'DriverDetail',
        component: DriverDetailPage,
        meta: { perm: ['driver.read', 'driver.read_own'], title: 'Fahrer-Detail' },
      },
      {
        path: 'drivers/:id/edit',
        name: 'DriverEdit',
        component: DriverEditPage,
        meta: { perm: 'driver.write', title: 'Fahrer bearbeiten' },
      },
      {
        path: 'fleet',
        name: 'Fleet',
        component: VehicleListPage,
        meta: { perm: 'vehicle.read', title: 'Flotte' },
      },
      {
        path: 'fleet/new',
        name: 'VehicleCreate',
        component: VehicleCreatePage,
        meta: { perm: 'vehicle.write', title: 'Neues Fahrzeug' },
      },
      {
        path: 'fleet/:id',
        name: 'VehicleDetail',
        component: VehicleDetailPage,
        meta: { perm: 'vehicle.read', title: 'Fahrzeug-Detail' },
      },
      {
        path: 'workshop',
        name: 'Workshop',
        component: WorkshopTaskListPage,
        meta: { perm: 'workshop_task.read', title: 'Werkstatt' },
      },
      {
        path: 'workshop/new',
        name: 'WorkshopTaskCreate',
        component: WorkshopTaskCreatePage,
        meta: { perm: 'workshop_task.write', title: 'Neue Werkstatt-Aufgabe' },
      },
      {
        path: 'workshop/:id',
        name: 'WorkshopTaskDetail',
        component: WorkshopTaskDetailPage,
        meta: { perm: 'workshop_task.read', title: 'Werkstatt-Aufgabe' },
      },
      {
        path: 'damage',
        name: 'Damage',
        component: DamageListPage,
        meta: { perm: 'damage.read', title: 'Schäden' },
      },
      {
        path: 'damage/:id',
        name: 'DamageDetail',
        component: DamageDetailPage,
        meta: { perm: 'damage.read', title: 'Schaden-Detail' },
      },
      {
        path: 'tickets',
        name: 'Tickets',
        component: TicketListPage,
        meta: { perm: 'ticket.read', title: 'Tickets' },
      },
      {
        path: 'tickets/:id',
        name: 'TicketDetail',
        component: TicketDetailPage,
        meta: { perm: 'ticket.read', title: 'Ticket-Detail' },
      },
      {
        path: 'fuel',
        name: 'Fuel',
        component: FuelListPage,
        meta: { perm: 'fuel.read', title: 'Tank-Belege' },
      },
      {
        path: 'fuel/:id',
        name: 'FuelDetail',
        component: FuelDetailPage,
        meta: { perm: 'fuel.read', title: 'Tank-Beleg' },
      },
      {
        path: 'dispatcher',
        name: 'Dispatcher',
        component: DispatcherCockpitPage,
        meta: { perm: 'attendance.read', title: 'Disposition' },
      },
      {
        path: 'documents',
        name: 'Documents',
        component: DocumentsListPage,
        meta: { perm: 'document.read', title: 'Dokumente' },
      },
      {
        path: 'audit',
        name: 'Audit',
        component: AuditListPage,
        meta: { perm: ['audit.read', 'audit.read_all'], title: 'Audit-Log' },
      },
      {
        path: 'stations',
        name: 'Stations',
        component: StationsListPage,
        meta: { perm: 'station.read', title: 'Stationen' },
      },
      {
        path: 'accommodations',
        name: 'Accommodations',
        component: AccommodationListPage,
        meta: { perm: 'accommodation.read', title: 'Unterkünfte' },
      },
      {
        path: 'accommodations/:id',
        name: 'AccommodationDetail',
        component: AccommodationDetailPage,
        meta: { perm: 'accommodation.read', title: 'Unterkunft' },
      },
      {
        path: 'sims',
        name: 'Sims',
        component: SimsListPage,
        meta: { perm: 'sim.read', title: 'SIM-Karten' },
      },
      {
        path: 'sims/:id',
        name: 'SimDetail',
        component: SimDetailPage,
        meta: { perm: 'sim.read', title: 'SIM-Detail' },
      },
      {
        path: 'payroll',
        name: 'Payroll',
        component: PayrollListPage,
        meta: { perm: 'payroll.read', title: 'Payroll' },
      },
      {
        path: 'payroll/:id',
        name: 'PayrollDetail',
        component: PayrollDetailPage,
        meta: { perm: 'payroll.read', title: 'Payroll-Run' },
      },
      {
        path: 'reporting',
        name: 'Reporting',
        component: ReportingPage,
        meta: { perm: 'reporting.read', title: 'Reporting' },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: SettingsPage,
        meta: { title: 'Einstellungen' },
      },
    ],
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: ForbiddenPage,
    meta: { public: true, layout: 'blank', title: 'Keine Berechtigung' },
  },
  {
    path: '/:catchAll(.*)',
    name: 'NotFound',
    component: NotFoundPage,
    meta: { public: true, layout: 'blank', title: 'Nicht gefunden' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const meta = (to.meta ?? {});

  if (meta.public) return true;

  if (!auth.isAuthenticated) {
    return { name: 'Login', query: { next: to.fullPath } };
  }

  // First-time after-reload: tokens exist but we don't have /me yet.
  if (auth.isAuthenticated && !auth.me) {
    try {
      await auth.loadMe();
    } catch {
      auth.forceLogout('me_failed');
      return { name: 'Login', query: { next: to.fullPath } };
    }
  }

  if (!auth.hasOfficeAccess) {
    return { name: 'NoOffice' };
  }

  if (meta.perm && !auth.hasPermission(meta.perm)) {
    return { name: 'Forbidden', query: { perm: Array.isArray(meta.perm) ? meta.perm.join(',') : meta.perm } };
  }

  return true;
});

router.afterEach((to) => {
  const meta = (to.meta ?? {});
  if (meta.title) {
    document.title = `${meta.title} · jatrendo`;
  } else {
    document.title = 'jatrendo';
  }
});
